import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DocumentManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const documents = [
    {
      id: 1,
      name: 'EUA Registry Verification Certificate',
      category: 'EUA',
      type: 'certificate',
      size: '2.4 MB',
      uploadDate: '2025-09-20T14:30:00Z',
      expiryDate: '2026-09-20',
      status: 'active',
      version: '1.2',
      uploadedBy: 'Sarah Chen',
      description: 'Official verification certificate for EUA registry account',
      tags: ['verification', 'registry', 'certificate'],
      accessLevel: 'restricted'
    },
    {
      id: 2,
      name: 'CFTC Position Report Q3 2025',
      category: 'Oil',
      type: 'report',
      size: '1.8 MB',
      uploadDate: '2025-09-19T16:45:00Z',
      expiryDate: null,
      status: 'active',
      version: '2.0',
      uploadedBy: 'David Kim',
      description: 'Quarterly position report submitted to CFTC',
      tags: ['cftc', 'position', 'quarterly'],
      accessLevel: 'public'
    },
    {
      id: 3,
      name: 'Risk Management Policy Update',
      category: 'General',
      type: 'policy',
      size: '3.2 MB',
      uploadDate: '2025-09-18T10:15:00Z',
      expiryDate: '2026-09-18',
      status: 'active',
      version: '3.1',
      uploadedBy: 'James Thompson',
      description: 'Updated risk management policies and procedures',
      tags: ['risk', 'policy', 'procedures'],
      accessLevel: 'internal'
    },
    {
      id: 4,
      name: 'MiFID II Transaction Records',
      category: 'EUA',
      type: 'records',
      size: '5.7 MB',
      uploadDate: '2025-09-17T09:20:00Z',
      expiryDate: null,
      status: 'archived',
      version: '1.0',
      uploadedBy: 'Michael Rodriguez',
      description: 'Transaction records for MiFID II compliance reporting',
      tags: ['mifid', 'transactions', 'compliance'],
      accessLevel: 'restricted'
    },
    {
      id: 5,
      name: 'AML Customer Due Diligence',
      category: 'General',
      type: 'compliance',
      size: '4.1 MB',
      uploadDate: '2025-09-16T13:30:00Z',
      expiryDate: '2026-09-16',
      status: 'pending_review',
      version: '1.5',
      uploadedBy: 'Emma Davis',
      description: 'Customer due diligence documentation for AML compliance',
      tags: ['aml', 'kyc', 'customer'],
      accessLevel: 'restricted'
    },
    {
      id: 6,
      name: 'Oil Futures Trading Agreement',
      category: 'Oil',
      type: 'contract',
      size: '2.9 MB',
      uploadDate: '2025-09-15T11:45:00Z',
      expiryDate: '2027-09-15',
      status: 'active',
      version: '2.3',
      uploadedBy: 'Lisa Wang',
      description: 'Master trading agreement for oil futures contracts',
      tags: ['trading', 'agreement', 'futures'],
      accessLevel: 'internal'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Documents', count: documents?.length },
    { value: 'EUA', label: 'EUA Trading', count: documents?.filter(doc => doc?.category === 'EUA')?.length },
    { value: 'Oil', label: 'Oil Futures', count: documents?.filter(doc => doc?.category === 'Oil')?.length },
    { value: 'General', label: 'General', count: documents?.filter(doc => doc?.category === 'General')?.length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10 border-success/20';
      case 'pending_review': return 'text-warning bg-warning/10 border-warning/20';
      case 'archived': return 'text-muted-foreground bg-muted border-border';
      case 'expired': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'certificate': return 'Award';
      case 'report': return 'FileText';
      case 'policy': return 'Shield';
      case 'records': return 'Database';
      case 'compliance': return 'CheckCircle';
      case 'contract': return 'FileSignature';
      default: return 'File';
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'public': return 'text-success';
      case 'internal': return 'text-accent';
      case 'restricted': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const filteredDocuments = documents?.filter(doc => {
      const matchesSearch = searchTerm === '' || 
        doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        doc?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        doc?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || doc?.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'date':
          return new Date(b.uploadDate) - new Date(a.uploadDate);
        case 'size':
          return parseFloat(b?.size) - parseFloat(a?.size);
        default:
          return 0;
      }
    });

  const formatFileSize = (size) => {
    return size;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString();
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="FolderOpen" size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Document Management</h2>
              <p className="text-sm text-muted-foreground">Secure storage and version control for compliance documents</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" iconName="Upload" iconPosition="left" size="sm">
              Upload Document
            </Button>
            <Button variant="outline" iconName="FolderPlus" iconPosition="left" size="sm">
              New Folder
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search documents, descriptions, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mt-4">
          {categories?.map(category => (
            <button
              key={category?.value}
              onClick={() => setSelectedCategory(category?.value)}
              className={`
                px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2
                ${selectedCategory === category?.value
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
            >
              <span>{category?.label}</span>
              <span className={`
                px-1.5 py-0.5 rounded text-xs font-medium
                ${selectedCategory === category?.value
                  ? 'bg-accent-foreground/20 text-accent-foreground'
                  : 'bg-background text-foreground'
                }
              `}>
                {category?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {filteredDocuments?.length > 0 ? (
          <div className="space-y-4">
            {filteredDocuments?.map(document => (
              <div key={document?.id} className="border border-border rounded-lg p-4 hover:shadow-subtle transition-shadow duration-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={getTypeIcon(document?.type)} size={20} className="text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-foreground truncate">{document?.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(document?.status)}`}>
                            {document?.status?.replace('_', ' ')}
                          </span>
                          {isExpiringSoon(document?.expiryDate) && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                              Expiring Soon
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{document?.description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={12} />
                            Uploaded: {formatDate(document?.uploadDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="HardDrive" size={12} />
                            {formatFileSize(document?.size)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="User" size={12} />
                            {document?.uploadedBy}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="GitBranch" size={12} />
                            v{document?.version}
                          </span>
                          <span className={`flex items-center gap-1 ${getAccessLevelColor(document?.accessLevel)}`}>
                            <Icon name="Lock" size={12} />
                            {document?.accessLevel}
                          </span>
                          {document?.expiryDate && (
                            <span className="flex items-center gap-1">
                              <Icon name="Clock" size={12} />
                              Expires: {formatDate(document?.expiryDate)}
                            </span>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1">
                          {document?.tags?.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" iconName="Download" />
                    <Button variant="ghost" size="sm" iconName="Eye" />
                    <Button variant="ghost" size="sm" iconName="Share" />
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
            <p className="text-muted-foreground">Try adjusting your search terms or category filter.</p>
          </div>
        )}

        {/* Pagination */}
        {filteredDocuments?.length > 0 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            <span className="text-sm text-muted-foreground">
              Showing {filteredDocuments?.length} of {documents?.length} documents
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" iconName="ChevronLeft" disabled />
              <span className="px-2 text-sm text-muted-foreground">Page 1 of 1</span>
              <Button variant="ghost" size="sm" iconName="ChevronRight" disabled />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentManagement;